import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import apiService from '../../services/ApiService';
import PageLoader from '../ui/PageLoader';
import EmptyState from '../ui/EmptyState';
import Button from '../ui/Button';
import { PageHeader } from '../ui/PageHeader';
import PageShell from '../ui/PageShell';
import {
  getPassportEncounterDetail,
  savePassportEncounterMemory,
  submitPassportScanAppeal,
  type PassportEncounterDetail as EncounterDetailData,
} from '../../services/api/passport';
import {
  SUBJECT_TYPE_LABELS,
  encounterStatusLabel,
  formatEncounterDateTime,
  METHOD_LABELS,
  riskStatusLabel,
  verificationLevelLabel,
} from './passportCopy';

const PassportEncounterDetailPage: React.FC = () => {
  const { publicId } = useParams<{ publicId: string }>();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const [encounter, setEncounter] = useState<EncounterDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [caption, setCaption] = useState('');
  const [visibility, setVisibility] = useState<'private' | 'public'>('private');
  const [savingMemory, setSavingMemory] = useState(false);
  const [appealReason, setAppealReason] = useState('');
  const [appealOpen, setAppealOpen] = useState(false);
  const [submittingAppeal, setSubmittingAppeal] = useState(false);

  const load = useCallback(async () => {
    if (!publicId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getPassportEncounterDetail(publicId);
      setEncounter(data);
      setCaption(data.memory_caption || '');
      setVisibility(data.memory_visibility || 'private');
      void apiService.trackEvent('passport_encounter_opened', {
        metadata: { subject_type: data.subject_type, status: data.status },
      });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Encounter not found');
    } finally {
      setLoading(false);
    }
  }, [publicId]);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate('/login', { state: { from: `/passport/encounters/${publicId || ''}` }, replace: true });
      return;
    }
    void load();
  }, [authLoading, isAuthenticated, navigate, publicId, load]);

  const handleSaveMemory = async () => {
    if (!publicId) return;
    setSavingMemory(true);
    try {
      await savePassportEncounterMemory(publicId, { caption, visibility });
      showToast('Memory saved.', 'success');
      void apiService.trackEvent('passport_memory_saved', { metadata: { visibility } });
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Failed to save memory', 'error');
    } finally {
      setSavingMemory(false);
    }
  };

  const handleSubmitAppeal = async () => {
    if (!publicId || !appealReason.trim()) return;
    setSubmittingAppeal(true);
    try {
      await submitPassportScanAppeal(publicId, appealReason.trim());
      showToast('Appeal submitted for review.', 'success');
      void apiService.trackEvent('passport_appeal_submitted');
      setAppealOpen(false);
      setAppealReason('');
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Failed to submit appeal', 'error');
    } finally {
      setSubmittingAppeal(false);
    }
  };

  if (authLoading || loading) {
    return <PageLoader message="Loading encounter…" />;
  }

  if (!isAuthenticated) {
    return null;
  }

  if (error || !encounter) {
    return (
      <PageShell contentClassName="!py-6 sm:!py-8">
        <PageHeader eyebrow="Bilt Passport" title="Encounter" backLink={{ label: 'Back to Passport', to: '/passport' }} />
        <EmptyState
          title="Couldn't load this encounter"
          description={error || 'This encounter may have been removed or is not yours.'}
          action={
            <Button type="button" variant="primary" onClick={() => void load()}>
              Try again
            </Button>
          }
        />
      </PageShell>
    );
  }

  const canAppeal = encounter.status === 'rejected' || encounter.status === 'revoked';

  return (
    <PageShell contentClassName="!py-6 sm:!py-8" width="md">
      <PageHeader
        eyebrow="Bilt Passport"
        title={encounter.subject?.title || SUBJECT_TYPE_LABELS[encounter.subject_type]}
        description={`${SUBJECT_TYPE_LABELS[encounter.subject_type]} · ${formatEncounterDateTime(encounter.occurred_at)}`}
        backLink={{ label: 'Back to Passport', to: '/passport' }}
      />

      <div className="card p-5 space-y-4 mb-6">
        <dl className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <dt className="text-meta">Status</dt>
            <dd className="text-white mt-1">{encounterStatusLabel(encounter.status)}</dd>
          </div>
          <div>
            <dt className="text-meta">Verification</dt>
            <dd className="text-white mt-1">{verificationLevelLabel(encounter.verification_level)}</dd>
          </div>
          <div>
            <dt className="text-meta">Method</dt>
            <dd className="text-white mt-1">{METHOD_LABELS[encounter.method] || encounter.method}</dd>
          </div>
          <div>
            <dt className="text-meta">Risk review</dt>
            <dd className="text-white mt-1">{riskStatusLabel(encounter.risk_status)}</dd>
          </div>
        </dl>

        {encounter.status === 'pending' ? (
          <div className="alert-warning">
            This encounter is awaiting validation. We'll never claim it happened until it's confirmed.
          </div>
        ) : null}
        {encounter.status === 'rejected' || encounter.status === 'revoked' ? (
          <div className="alert-error">
            This encounter wasn't verified. If you believe this is a mistake, you can appeal below.
          </div>
        ) : null}
      </div>

      <div className="form-section mb-6">
        <h2 className="form-section-title">Private memory</h2>
        <p className="text-xs text-gray-500">
          Optional. Only visible to you unless you choose to make it public.
        </p>
        <textarea
          className="input input-textarea w-full"
          rows={3}
          maxLength={500}
          placeholder="What do you remember about this one?"
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
        />
        <div className="flex items-center gap-3">
          <label className="inline-flex items-center gap-2 text-sm text-gray-300">
            <input
              type="checkbox"
              className="checkbox-control"
              checked={visibility === 'public'}
              onChange={(e) => setVisibility(e.target.checked ? 'public' : 'private')}
            />
            Make this note public
          </label>
        </div>
        <Button type="button" variant="primary" size="sm" loading={savingMemory} onClick={() => void handleSaveMemory()}>
          Save memory
        </Button>
      </div>

      {canAppeal ? (
        <div className="form-section">
          <h2 className="form-section-title">Appeal this decision</h2>
          {!appealOpen ? (
            <Button type="button" variant="secondary" size="sm" onClick={() => setAppealOpen(true)}>
              Start an appeal
            </Button>
          ) : (
            <>
              <textarea
                className="input input-textarea w-full"
                rows={3}
                placeholder="Tell us why you think this should be reconsidered."
                value={appealReason}
                onChange={(e) => setAppealReason(e.target.value)}
              />
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  loading={submittingAppeal}
                  disabled={!appealReason.trim()}
                  onClick={() => void handleSubmitAppeal()}
                >
                  Submit appeal
                </Button>
                <Button type="button" variant="ghost" size="sm" onClick={() => setAppealOpen(false)}>
                  Cancel
                </Button>
              </div>
            </>
          )}
        </div>
      ) : null}
    </PageShell>
  );
};

export default PassportEncounterDetailPage;
